/**
 * @file ImageWrapper.tsx
 * @description Optimized image component with CMS integration, responsive sizing, and fallback handling.
 * Wraps Next.js Image with enhanced features for Contentstack images.
 */

'use client';

import { JSX, useCallback, useMemo, useState } from 'react';
import Image from 'next/image';
import { imageWrapperVariants } from './ImageWrapper.styles';
import { IEnhancedImage } from '@/.generated';
import { getCSLPAttributes } from '@/utils/type-guards';
import { withDeliveryParams } from '@/utils/image-utils';
import DefaultFallbackImage from '@/public/images/default-fallback-image.webp';
import { cn } from 'tailwind-variants';

// Static image imports resolve to StaticImageData with the Next image-types ref, but to a plain URL string without it (e.g. CI before a next build); normalize to a URL string either way
const fallbackImageSrc: string =
  typeof DefaultFallbackImage === 'string' ? DefaultFallbackImage : DefaultFallbackImage.src;

export interface ImageWrapperProps
  extends Omit<React.ComponentProps<typeof Image>, 'src' | 'alt' | 'width' | 'height'> {
  /** Additional CSS classes for wrapper */
  wrapperClassName?: string;
  /** Additional CSS classes for image */
  imageClassName?: string;
  /** Enhanced image object from CMS */
  image?: IEnhancedImage | null;
  /** Overrides the CMS-derived accessible name; pass `alt=""` for decorative images */
  alt?: string;
  /** Responsive sizes attribute */
  sizes?: string;
  /** Image quality (1-100) */
  quality?: number;
  /** Inline styles for wrapper */
  style?: React.CSSProperties;
  /** Image fills parent container */
  fill?: boolean;
  /** FetchPriority strategy */
  fetchPriority?: 'high' | 'low' | 'auto';
  /** Loading strategy */
  loading?: 'lazy' | 'eager';
  /** Placeholder type */
  placeholder?: 'blur' | 'empty';
  /** Base64 blur placeholder */
  blurDataURL?: string;
  /** Show fallback on error */
  showFallbackImage?: boolean;
  /** Full-bleed layout (edge-to-edge) */
  isFullBleed?: boolean;
  /** Explicit image width — overrides default 100% style */
  width?: number | string;
  /** Explicit image height — overrides default 100% style */
  height?: number | string;
  /** Children elements (specifically used when image needs to be background image) */
  children?: React.ReactNode;
}

/** Internal props for Next.js Image component */
export interface NextImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  alt: string;
  className?: string;
  preload?: boolean;
  sizes?: string;
  height?: number;
  width?: number;
  style: React.CSSProperties;
  fill?: boolean;
  quality?: number;
  fetchPriority?: 'high' | 'low' | 'auto';
  loading?: 'lazy' | 'eager';
  placeholder?: 'blur' | 'empty';
  blurDataURL?: string;
  onError?: () => void;
  onLoad?: () => void;
}

/**
 * Validates image dimensions, ensuring they are positive numbers.
 * Converts string inputs to numbers.
 */
const validateDimensions = (width?: number | null, height?: number | null) => {
  return width || height;
};

// Convert a numeric dimension to px so setProperty gets a valid value (mirrors React's inline-style behavior)
const toCssDimension = (value: string | number): string =>
  typeof value === 'number' ? `${value}px` : value;

/**
 * Generates optimal sizes attribute for responsive images.
 * Tells the browser which image size to download based on viewport width.
 */
const getOptimalSizes = (
  customSizes?: string,
  imageWidth?: number,
  responsive?: boolean
): string => {
  if (customSizes) return customSizes;
  if (!responsive && imageWidth) {
    return `(max-width: ${imageWidth}px) 100vw, ${imageWidth}px`;
  }
  return '(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw';
};

/**
 * Optimized image component with CMS integration and responsive sizing.
 * Handles Contentstack images with automatic fallbacks, responsive behavior,
 * and Next.js Image optimization.
 *
 * @example
 * <ImageWrapper image={cmsImage} priority sizes="(max-width: 768px) 100vw, 50vw" />
 */
const ImageWrapper = ({
  wrapperClassName,
  imageClassName,
  image,
  alt,
  quality = 75,
  sizes,
  fill,
  fetchPriority = 'auto',
  loading = 'lazy',
  placeholder,
  blurDataURL,
  showFallbackImage = true,
  isFullBleed = false,
  width,
  height,
  children,
  ...rest
}: ImageWrapperProps): JSX.Element => {
  const [isError, setIsError] = useState(false);

  // Extract CMS image data (safe to access conditionally inside hooks)
  const title = image?.image?.title;
  const url = image?.image?.url;
  const dimension = image?.image?.dimension;
  const alternate_text = image?.alternate_text;
  const image_fit_options = image?.image_fit_options;
  const image_position_options = image?.image_position_options;
  const dimensions = image?.custom_dimensions;
  const rounded_image = image?.rounded_image;

  const hasCustomDimensions =
    !fill && url && validateDimensions(dimensions?.image_width, dimensions?.image_height);

  // Generate optimal sizes attribute
  const optimalSizes = useMemo(
    () => getOptimalSizes(sizes, dimension?.width, !hasCustomDimensions),
    [sizes, dimension?.width, hasCustomDimensions]
  );

  // Target render width used to ask Contentstack for a right-sized asset instead of the full original
  const deliveryWidth = dimensions?.image_width ?? dimension?.width ?? undefined;

  // Build Next.js Image props
  const nextImageProps: NextImageProps = useMemo(() => {
    const clampedQuality = Math.max(1, Math.min(100, quality));
    const props: NextImageProps = {
      // `??` not `||` — an explicit alt="" marks a decorative image and must survive
      alt: alt ?? (alternate_text || title || 'Image'),
      fetchPriority,
      sizes: optimalSizes,
      src: isError
        ? fallbackImageSrc
        : (withDeliveryParams(url, { width: deliveryWidth, quality: clampedQuality }) ?? ''),
      style: {},
      quality: clampedQuality,
      loading,
    };

    if (placeholder === 'blur' && blurDataURL) {
      props.placeholder = 'blur';
      props.blurDataURL = blurDataURL;
    } else if (placeholder) {
      props.placeholder = placeholder;
    }

    if (fill) {
      props.fill = true;
    } else {
      props.height = dimension?.height;
      props.width = dimension?.width;
    }

    if (image_fit_options) {
      props.style.objectFit = image_fit_options;
    }

    if (image_position_options) {
      props.style.objectPosition = image_position_options;
    }

    return props;
  }, [
    alt,
    alternate_text,
    title,
    fetchPriority,
    optimalSizes,
    url,
    deliveryWidth,
    quality,
    loading,
    placeholder,
    blurDataURL,
    fill,
    image_fit_options,
    image_position_options,
    isError,
    dimension?.width,
    dimension?.height,
  ]);

  const { wrapperBase, fallbackImageBase, imageBase } = imageWrapperVariants({
    isFill: !!fill,
    roundedImage: !!rounded_image,
    isFullBleed: !!isFullBleed,
  });

  const wrapperStyle = useMemo(() => {
    if (hasCustomDimensions) {
      return {
        width: dimensions?.image_width || '100%',
        height: dimensions?.image_height || 'auto',
      };
    }
    if (width || height) {
      return {
        width: width ?? '100%',
        height: height ?? '100%',
      };
    }
    return undefined;
  }, [hasCustomDimensions, width, height, dimensions?.image_height, dimensions?.image_width]);

  // Re-affirm wrapper width/height as inline !important so host utilities (e.g. Bootstrap .w-*, img reboot) can't override sizing
  const applyWrapperImportant = useCallback(
    (node: HTMLDivElement | null) => {
      if (!node) return;
      if (wrapperStyle?.width != null) {
        node.style.setProperty('width', toCssDimension(wrapperStyle.width), 'important');
      }
      if (wrapperStyle?.height != null) {
        node.style.setProperty('height', toCssDimension(wrapperStyle.height), 'important');
      }
    },
    [wrapperStyle]
  );

  if (!image || !url) {
    return <></>;
  }

  return (
    <div
      ref={applyWrapperImportant}
      className={wrapperBase({ class: wrapperClassName })}
      style={wrapperStyle}
    >
      {!isError && (
        <Image
          data-component="helpers/fieldwrappers/imagewrapper"
          {...nextImageProps}
          {...rest}
          // Resizing already happens via withDeliveryParams on the CDN; skip Next's own /_next/image
          // pass so cold-isolate re-encoding (no persistent cache on Launch) can't 500 under load
          unoptimized
          className={cn(imageBase(), imageClassName)}
          onError={() => setIsError(true)}
          {...getCSLPAttributes(image.$?.image)}
        />
      )}

      {isError && showFallbackImage && (
        <Image
          src={DefaultFallbackImage}
          alt="Default Fallback Image"
          className={fallbackImageBase()}
          unoptimized={true}
        />
      )}
      {children}
    </div>
  );
};

export default ImageWrapper;
